import React from 'react';
import { ExternalLink } from 'lucide-react';

export interface LinkPreviewCardProps {
  url: string;
  title?: string;
  description?: string;
  imageUrl?: string;
  siteName?: string;
  variant?: 'tech' | 'purple';
}

/**
 * Rich URL card — cover image + title + hostname (OG-style).
 */
const LinkPreviewCard: React.FC<LinkPreviewCardProps> = ({
  url,
  title,
  description,
  imageUrl,
  siteName,
  variant = 'tech',
}) => {
  const host =
    siteName ||
    (() => {
      try {
        return new URL(url).hostname.replace(/^www\./, '');
      } catch {
        return 'Link';
      }
    })();

  const border =
    variant === 'tech'
      ? 'border-tech-border bg-tech-gray/40'
      : 'border-white/15 bg-white/5';

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`block rounded-xl overflow-hidden border ${border} hover:border-tech-cyan/40 transition-all group`}
    >
      {imageUrl ? (
        <div className="aspect-[1.91/1] w-full bg-tech-darker overflow-hidden">
          <img
            src={imageUrl}
            alt=""
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      ) : (
        <div className="aspect-[1.91/1] w-full bg-gradient-to-br from-tech-blue/30 via-tech-cyan/20 to-tech-accent/20 flex items-center justify-center">
          <ExternalLink className="w-12 h-12 text-tech-cyan/60" aria-hidden />
        </div>
      )}
      <div className="p-4">
        <p className="text-xs uppercase tracking-wide text-tech-text-muted mb-1">{host}</p>
        <p className="font-semibold text-tech-text line-clamp-2 group-hover:text-tech-cyan transition-colors">
          {title || url}
        </p>
        {description && (
          <p className="text-sm text-tech-text-muted mt-1 line-clamp-2">{description}</p>
        )}
      </div>
    </a>
  );
};

export default LinkPreviewCard;
